import {
  menu,
  popFilterableSimpleMenu,
  type PopupTarget,
} from '@blocksuite/affine-components/context-menu';
import {
  CopyIcon,
  DeleteIcon,
  ExpandFullIcon,
  MathPanelIcon,
  MoveLeftIcon,
  MoveRightIcon,
} from '@blocksuite/icons/lit';
import { html } from 'lit';

import type { Property } from '../../../../core/view-manager/property.js';
import { openRowFormulaMenu } from '../../../../property-presets/formula/row-menu.js';
import type { FormulaPropertyData } from '../../../../property-presets/formula/types.js';
import { TableViewRowSelection } from '../../selection';
import type { TableSelectionController } from '../controller/selection';
import type { VirtualTableViewUILogic } from '../table-view-ui-logic';

export const openDetail = (
  tableViewLogic: VirtualTableViewUILogic,
  rowId: string,
  selection: TableSelectionController
) => {
  const old = selection.selection;
  selection.selection = undefined;
  tableViewLogic.root.openDetailPanel({
    view: tableViewLogic.view,
    rowId: rowId,
    onClose: () => {
      selection.selection = old;
    },
  });
};

export const popRowMenu = (
  tableViewLogic: VirtualTableViewUILogic,
  ele: PopupTarget,
  selectionController: TableSelectionController
) => {
  const selection = selectionController.selection;
  if (!TableViewRowSelection.is(selection)) {
    return;
  }
  if (selection.rows.length > 1) {
    const rows = TableViewRowSelection.rowsIds(selection);
    popFilterableSimpleMenu(ele, [
      menu.group({
        name: '',
        items: [
          menu.action({
            name: 'Copy',
            prefix: html` <div
              style="transform: rotate(90deg);display:flex;align-items:center;"
            >
              ${CopyIcon()}
            </div>`,
            select: () => {
              tableViewLogic.clipboardController.copy();
            },
          }),
        ],
      }),
      menu.group({
        name: '',
        items: [
          menu.action({
            name: 'Delete Rows',
            class: {
              'delete-item': true,
            },
            prefix: DeleteIcon(),
            select: () => {
              selectionController.view.rowsDelete(rows);
              selectionController.logic.ui$.value?.requestUpdate();
            },
          }),
        ],
      }),
    ]);
    return;
  }
  const row = selection.rows[0];
  if (!row) return;
  const view = selectionController.view;
  const formulaColumns = view.propertiesRaw$.value.filter(
    property => property.type$.value === 'formula'
  );
  const rowModel = view.rowGetOrCreate(row.id);
  const setRowFormulaOverride =
    (propertyId: string) => (value: string | null) => {
      const cell = rowModel.cells$.value.find(c => c.propertyId === propertyId);
      cell?.valueSet(value);
    };
  const openRowFormula = (column: (typeof formulaColumns)[number]) => {
    const property = view.propertyGetOrCreate(column.id) as unknown as Property<
      unknown,
      unknown,
      FormulaPropertyData
    >;
    openRowFormulaMenu(
      ele,
      view,
      property,
      row.id,
      setRowFormulaOverride(column.id)
    );
  };
  popFilterableSimpleMenu(ele, [
    menu.action({
      name: 'Expand Row',
      prefix: ExpandFullIcon(),
      select: () => {
        openDetail(tableViewLogic, row.id, selectionController);
      },
    }),
    menu.group({
      name: '',
      items: [
        menu.action({
          name: 'Insert Before',
          prefix: html` <div
            style="transform: rotate(90deg);display:flex;align-items:center;"
          >
            ${MoveLeftIcon()}
          </div>`,
          select: () => {
            selectionController.insertRowBefore(row.groupKey, row.id);
          },
        }),
        menu.action({
          name: 'Insert After',
          prefix: html` <div
            style="transform: rotate(90deg);display:flex;align-items:center;"
          >
            ${MoveRightIcon()}
          </div>`,
          select: () => {
            selectionController.insertRowAfter(row.groupKey, row.id);
          },
        }),
      ],
    }),
    ...(formulaColumns.length
      ? [
          menu.group({
            items:
              formulaColumns.length === 1
                ? [
                    menu.action({
                      name: 'Formula for this row…',
                      prefix: MathPanelIcon(),
                      select: () => {
                        const column = formulaColumns[0];
                        if (column) {
                          openRowFormula(column);
                        }
                      },
                    }),
                  ]
                : [
                    menu.subMenu({
                      name: 'Formula for this row…',
                      prefix: MathPanelIcon(),
                      options: {
                        items: [
                          menu.group({
                            items: formulaColumns.map(column =>
                              menu.action({
                                name: column.name$.value,
                                select: () => openRowFormula(column),
                              })
                            ),
                          }),
                        ],
                      },
                    }),
                  ],
          }),
        ]
      : []),
    menu.group({
      items: [
        menu.action({
          name: 'Delete Row',
          class: { 'delete-item': true },
          prefix: DeleteIcon(),
          select: () => {
            selectionController.deleteRow(row.id);
          },
        }),
      ],
    }),
  ]);
};
